import cloneDeep from 'lodash/cloneDeep';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watchEffect } from 'vue';
import { useEventEmitter } from '@/composable/useEventEmitter';
import { useLocalStorage } from '@/composable/useLocalStorage';
import { useSessionStorage } from '@/composable/useSessionStorage';
import { Api, fireAndForget } from '@/core/lib/api';
import { convertToEuroPrice } from '@/core/lib/oddsHelper';
import { pick } from '@/core/lib/utils';
import { EnumHandicapType, EnumOddsStatus } from '@/core/oddsApi/oddsApiType';
import type { IBetSlip, ITicket } from '@/interface/IBetSlip';
import { BetSlipType } from '@/interface/IBetSlip';
import type { IDynamicMarketBetSlip } from '@/interface/IDynamicMarketBetSlip';
import type { IEvent, ITicketEvent } from '@/interface/IEvent';
import type { Score } from '@/interface/ILiveScore';
import type { IMarket, IMarketSelection } from '@/interface/IMarket';
import type { IOdds, IPrice } from '@/interface/IOdds';
import type { IOutrightLeague } from '@/interface/IOutrightLeague';
import type { IOutrightOdds } from '@/interface/IOutrightOdds';
import type { IBetBuilderBetSlip, IBetBuilderMarket, IBetBuilderTicket, ISelection } from '@/interface/betBuilder';
import {
    HandicapType,
    MarketType,
    OddsOption,
    OpenTicketStatus,
    PriceStyle,
    SiteStyle,
    SportType,
} from '@/interface/enum';
import type { AsiaBetPageType } from '@/interface/enum';
import { useCustomerStore } from '@/store/customerStore';
import { useToggleStore } from '@/store/toggleStore';

const pickSingleKeys = ['event', 'odds', 'priceOption', 'betPage'] as const;
type SessionSingleTicket = Pick<ITicket, typeof pickSingleKeys[number]>;
const pickBetBuilderKeys = ['match', 'market', 'selection', 'betPage'] as const;
type SessionBetBuilderTicket = Pick<IBetBuilderTicket, typeof pickBetBuilderKeys[number]>;
const pickDynamicMarketKeys = ['selectionId', 'marketId', 'matchId', 'leagueId', 'leagueSize', 'isLive', 'isSimulation', 'betPage', 'display', 'selection'] as const;
type SessionDynamicMarketTicket = Pick<IDynamicMarketBetSlip, typeof pickDynamicMarketKeys[number]>;

const getDefaultBetSlip = (type: BetSlipType.Single | BetSlipType.Parlay | BetSlipType.Outright): IBetSlip => ({
    type,
    uid: '',
    minBet: 0,
    maxBet: 0,
    balance: 0,
    message: OpenTicketStatus.Success,
    tickets: [],
});

const getDefaultEvent = (league: IEvent['league']): IEvent => (
    {
        id: 0,
        eventCode: '',
        eventDate: new Date(),
        hasStatistics: false,
        liveCourtMatchId: 0,
        showTime: new Date(),
        showTimeType: 0,
        marketTypeCount: {},
        isLive: false,
        isSimulation: false,
        isTvLive: false,
        isSboLive: false,
        tvChannels: [],
        absKickOffTime: new Date(),
        eventResults: [],
        handicapType: EnumHandicapType.None,
        isMixParlay: false,
        extraInfo: { injuryTime: 0, period: 0, round: 0, statisticsUrl: '', periodStartTime: '' },
        awayTeam: { name: [], redCard: 0, liveScore: 0, isFavorite: false },
        homeTeam: { name: [], redCard: 0, liveScore: 0, isFavorite: false },
        league,
        sportType: SportType.Unknown,
        stream: { videoUrl: null, betRadar: null, gLive: null, betConstruct: null, betGenius: null },
        outrightIds: [],
        realKickoffTime: new Date(),
    }
);

const getDefaultOdds = (): IOdds => (
    {
        id: 0,
        isLive: false,
        hasMixParlay: false,
        point: 0,
        handicapType: HandicapType.Unknown,
        status: EnumOddsStatus.Unknown,
        betCondition: '',
        sortOrder: 0,
        sportType: SportType.Unknown,
        marketType: MarketType.Unknown,
        prices: [],
        eventResult: {
            id: 0,
            eventId: 0,
            handicapType: HandicapType.Unknown,
            marketGroup: {
                id: 0,
                name: [],
                awayExtension: [],
                awayTemplate: '',
                displayPriority: 0,
                homeExtension: [],
                homeTemplate: '',
                isSingle: false,
                isYesNo: false,
            },
            liveHomeScore: 0,
            liveAwayScore: 0,
        },
    }
);

export const useBetSlipStore = defineStore('BetSlip', () => {
    const eventEmitter = useEventEmitter();
    const { accountId } = storeToRefs(useCustomerStore());
    const [sessionSingleTicket, setSessionSingleTicket] = useSessionStorage<SessionSingleTicket | null>(
        `singleTicket:${accountId.value}`,
        null,
        {
            decode: v => JSON.parse(v),
            encode: v => JSON.stringify(v),
        },
    );

    const [sessionParlayTicket, setSessionParlayTicket] = useSessionStorage<SessionSingleTicket[] | null>(
        `parlayTicket:${accountId.value}`,
        null,
        {
            decode: v => JSON.parse(v),
            encode: v => JSON.stringify(v),
        },
    );

    const [sessionBetBuilderTicket, setSessionBetBuilderTicket] = useSessionStorage<SessionBetBuilderTicket[] | null>(
        `betBuilderTicket:${accountId.value}`,
        null,
        {
            decode: v => JSON.parse(v),
            encode: v => JSON.stringify(v),
        },
    );

    const [sessionDynamicMarketTicket, setSessionDynamicMarketTicket] = useSessionStorage<SessionDynamicMarketTicket | null>(
        `dynamicMarketTicket:${accountId.value}`,
        null,
        {
            decode: v => JSON.parse(v),
            encode: v => JSON.stringify(v),
        },
    );

    const [isShowLastStake, setIsShowLastStake] = useLocalStorage<boolean>(`lastStake:isShowLastStake:${accountId.value}`, false);
    const [singleLastStake, setSingleLastStake] = useLocalStorage<number>(`lastStake:single:${accountId.value}`, 0);
    const [parlayLastStake, setParlayLastStake] = useLocalStorage<number>(`lastStake:parlay:${accountId.value}`, 0);

    const singleBetSlip = ref<IBetSlip | null>(null);
    const singleTicket = computed<ITicket | null>(() => {
        if (!singleBetSlip.value) return null;
        return singleBetSlip.value.tickets[0];
    });
    const parlayBetSlip = ref<IBetSlip | null>(null);
    const betBuilderBetSlip = ref<IBetBuilderBetSlip | null>(null);
    const dynamicMarketBetSlip = ref<IDynamicMarketBetSlip | null>(null);

    const removeBetBuilderBetSlip = () => {
        betBuilderBetSlip.value = null;
    };

    const removeDynamicMarketBetSlip = () => {
        if (dynamicMarketBetSlip.value?.uid) {
            fireAndForget(Api.clearTicket());
        }
        dynamicMarketBetSlip.value = null;
    };

    const removeSingleBetSlip = () => {
        if (singleBetSlip.value) {
            fireAndForget(Api.clearTicket());
            singleBetSlip.value = null;
        }
    };

    const clearExclusiveBetSlips = (except: 'single' | 'betBuilder' | 'dynamicMarket') => {
        if (except !== 'single') removeSingleBetSlip();
        if (except !== 'betBuilder') removeBetBuilderBetSlip();
        if (except !== 'dynamicMarket') removeDynamicMarketBetSlip();
    };

    const addToSingleBetSlip = (event: ITicketEvent, odds: IOdds, priceOption: IPrice, betPage: AsiaBetPageType, type = BetSlipType.Single | BetSlipType.Outright) => {
        clearExclusiveBetSlips('single');

        singleBetSlip.value = {
            ...getDefaultBetSlip(type),
            tickets: [{
                key: `${odds.id}-${priceOption.option}`,
                betPage,
                priceStyle: PriceStyle.Unknown,
                event: cloneDeep(event),
                odds: cloneDeep(odds),
                priceOption: cloneDeep(priceOption),
            }],
        };
    };

    function getParlayTicketKey(odds: IOdds, priceOption: IPrice) {
        return `${odds.id}-${priceOption.option}`;
    }

    function createParlayTicket(
        event: ITicketEvent,
        odds: IOdds,
        priceOption: IPrice,
        betPage: AsiaBetPageType,
        priceStyle?: PriceStyle,
    ): ITicket {
        return {
            key: getParlayTicketKey(odds, priceOption),
            betPage,
            priceStyle: priceStyle ?? PriceStyle.Unknown,
            event: cloneDeep(event),
            odds: cloneDeep(odds),
            priceOption: cloneDeep(priceOption),
        };
    }

    const addToParlayBetSlip = (ticket: ITicket) => {
        parlayBetSlip.value ??= {
            ...getDefaultBetSlip(BetSlipType.Parlay),
        };

        parlayBetSlip.value = {
            ...parlayBetSlip.value,
            tickets: [
                ...parlayBetSlip.value.tickets.filter(t => t.event.id !== ticket.event.id),
                ticket,
            ],
        };

        eventEmitter.emit('betSlip:addToParlayBetSlip');
    };

    function setParlayBetSlipTickets(tickets: ITicket[]) {
        parlayBetSlip.value ??= {
            ...getDefaultBetSlip(BetSlipType.Parlay),
        };

        parlayBetSlip.value = {
            ...parlayBetSlip.value,
            tickets,
        };

        eventEmitter.emit('betSlip:addToParlayBetSlip');
    }

    const addSingleToParlayBetSlip = (ticket: ITicket, priceStyle: PriceStyle) => {
        const newPriceOption = convertToEuroPrice(ticket.priceOption.price, priceStyle);
        addToParlayBetSlip({ ...ticket, priceOption: { ...ticket.priceOption, price: newPriceOption } });
    };

    const addToOutrightBetSlip = (league: IOutrightLeague, odds: IOutrightOdds, betPage: AsiaBetPageType) => {
        clearExclusiveBetSlips('single');
        const defaultEvent = getDefaultEvent({
            id: league.id,
            name: league.name,
            info: league.info,
            size: league.size,
        });
        singleBetSlip.value = {
            ...getDefaultBetSlip(BetSlipType.Outright),
            tickets: [{
                key: `${odds.id}-${OddsOption.OR}`,
                betPage,
                priceStyle: PriceStyle.Unknown,
                event: {
                    ...defaultEvent,
                    id: league.id,
                    sportType: league.sportType,
                    homeTeam: { ...defaultEvent.homeTeam, name: odds.teamName ?? '' },
                    awayTeam: { ...defaultEvent.awayTeam, name: odds.teamName ?? '' },
                },
                odds: {
                    ...getDefaultOdds(),
                    id: odds.id,
                    isLive: odds.isLive,
                    status: EnumOddsStatus.Running,
                    sportType: league.sportType,
                    marketType: MarketType.OutRight,
                    prices: [],
                },
                priceOption: {
                    price: odds.price,
                    option: OddsOption.OR,
                },
            }],
        };
    };

    const updateSingleBetSlip = (updateFn: (betSlip: IBetSlip) => IBetSlip) => {
        if (!singleBetSlip.value) return;
        singleBetSlip.value = updateFn({ ...singleBetSlip.value });
    };

    const updateParlayBetSlip = (updateFn: (parlayBetSlip: IBetSlip) => IBetSlip) => {
        if (!parlayBetSlip.value) return;
        parlayBetSlip.value = updateFn({ ...parlayBetSlip.value });
    };

    const removeParlayBetSlip = () => {
        parlayBetSlip.value = null;
    };

    const removeParlayTicket = (key: string) => {
        if (!parlayBetSlip.value) return;

        parlayBetSlip.value = {
            ...parlayBetSlip.value,
            tickets: parlayBetSlip.value.tickets.filter(t => t.key !== key),
        };

        if (parlayBetSlip.value.tickets.length === 0) {
            removeParlayBetSlip();
        }
    };

    const addToBetBuilderBetSlip = (match: IEvent, market: IBetBuilderMarket, selection: ISelection, _betPage: AsiaBetPageType) => {
        clearExclusiveBetSlips('betBuilder');

        const newTicket: IBetBuilderTicket = {
            key: selection.id.toString(),
            match,
            selection,
            sportType: match.sportType,
            market,
            betPage: _betPage,
        };

        const isInSameMatch = newTicket.match.id === betBuilderBetSlip.value?.tickets[0].match.id;
        if (betBuilderBetSlip.value && isInSameMatch) {
            betBuilderBetSlip.value = {
                ...betBuilderBetSlip.value,
                tickets: [
                    ...betBuilderBetSlip.value.tickets,
                    newTicket,
                ],
            };
        } else {
            betBuilderBetSlip.value = {
                type: BetSlipType.BetBuilder,
                uid: '',
                minBet: 0,
                maxBet: 0,
                balance: 0,
                tickets: [newTicket],
                price: { value: 0, style: PriceStyle.Euro },
                markets: [],
                message: OpenTicketStatus.Success,
                selectionLiveScores: new Map<number, Score>(),
                updatingStatus: 'none',
            };
        }
    };

    function setBetBuilderBetSlipTickets(tickets: IBetBuilderTicket[]) {
        removeSingleBetSlip();
        betBuilderBetSlip.value = {
            type: BetSlipType.BetBuilder,
            uid: '',
            minBet: 0,
            maxBet: 0,
            balance: 0,
            tickets,
            price: { value: 0, style: PriceStyle.Euro },
            markets: [],
            message: OpenTicketStatus.Success,
            selectionLiveScores: new Map<number, Score>(),
            updatingStatus: 'none',
        };
    }

    const updateBetBuilderBetSlip = (updateFn: (betSlip: IBetBuilderBetSlip) => IBetBuilderBetSlip) => {
        if (!betBuilderBetSlip.value) return;
        betBuilderBetSlip.value = updateFn({ ...betBuilderBetSlip.value });
    };

    const removeBetBuilderTicket = (key: string) => {
        if (!betBuilderBetSlip.value) return;

        betBuilderBetSlip.value = {
            ...betBuilderBetSlip.value,
            tickets: betBuilderBetSlip.value.tickets.filter(ticket => ticket.key !== key),
        };

        if (betBuilderBetSlip.value.tickets.length === 0) {
            betBuilderBetSlip.value = null;
        }
    };

    const addToDynamicMarketBetSlip = (event: IEvent, market: IMarket, selection: IMarketSelection, betPage: AsiaBetPageType) => {
        clearExclusiveBetSlips('dynamicMarket');
        dynamicMarketBetSlip.value = {
            type: BetSlipType.Dynamic,
            uid: '',
            minBet: 0,
            maxBet: 0,
            balance: null,
            selectionId: selection.id,
            marketId: market.id,
            matchId: selection.matchId,
            leagueId: event.league.id,
            leagueSize: event.league.size,
            isLive: event.isLive,
            isSimulation: event.isSimulation,
            betPage,
            selection: {
                price: {
                    value: selection.price.value,
                    priceStyle: selection.price.priceStyle ?? PriceStyle.Euro,
                },
                betCondition: selection.name,
                score: null,
            },
            display: {
                selectionName: selection.name,
                marketName: market.name,
                sportType: event.sportType,
                homeTeamName: event.homeTeam.name ?? [],
                awayTeamName: event.awayTeam.name ?? [],
                leagueName: event.league.name ?? [],
            },
        };
    };

    const updateDynamicMarketBetSlip = (updater: (betSlip: IDynamicMarketBetSlip) => IDynamicMarketBetSlip) => {
        if (!dynamicMarketBetSlip.value) return;
        dynamicMarketBetSlip.value = updater(dynamicMarketBetSlip.value);
    };

    if (sessionParlayTicket.value) {
        sessionParlayTicket.value.forEach((sessionTicket) => {
            const ticket = createParlayTicket(sessionTicket.event, sessionTicket.odds, sessionTicket.priceOption, sessionTicket.betPage);
            addToParlayBetSlip(ticket);
        });
    }

    if (sessionSingleTicket.value) {
        addToSingleBetSlip(
            sessionSingleTicket.value.event,
            sessionSingleTicket.value.odds,
            sessionSingleTicket.value.priceOption,
            sessionSingleTicket.value.betPage,
            sessionSingleTicket.value.odds.marketType === MarketType.OutRight ? BetSlipType.Outright : BetSlipType.Single,
        );
    }

    if (sessionBetBuilderTicket.value) {
        sessionBetBuilderTicket.value.forEach(ticket => addToBetBuilderBetSlip(
            ticket.match,
            ticket.market,
            ticket.selection,
            ticket.betPage,
        ));
    }

    if (sessionDynamicMarketTicket.value) {
        const {
            selectionId, marketId, matchId, leagueId, leagueSize, isLive, isSimulation, betPage, display, selection,
        } = sessionDynamicMarketTicket.value;
        dynamicMarketBetSlip.value = {
            type: BetSlipType.Dynamic,
            uid: '',
            minBet: 0,
            maxBet: 0,
            balance: null,
            selectionId,
            marketId,
            matchId,
            leagueId,
            leagueSize,
            isLive,
            isSimulation: isSimulation ?? false,
            betPage,
            selection,
            display,
        };
    }

    watchEffect(() => {
        if (singleTicket.value) {
            setSessionSingleTicket(pick<ITicket>(singleTicket.value, pickSingleKeys));
        } else {
            setSessionSingleTicket(null);
        }
    });

    watchEffect(() => {
        if (parlayBetSlip.value) {
            setSessionParlayTicket(parlayBetSlip.value.tickets.map(ticket => pick<ITicket>(ticket, pickSingleKeys)));
        } else {
            setSessionParlayTicket(null);
        }
    });

    const { isBetBuilderEnabled } = storeToRefs(useToggleStore(SiteStyle.Asia));
    watchEffect(() => {
        if (!isBetBuilderEnabled.value) {
            setSessionBetBuilderTicket(null);
        } else if (betBuilderBetSlip.value) {
            setSessionBetBuilderTicket(betBuilderBetSlip.value.tickets.map(ticket => pick<IBetBuilderTicket>(ticket, pickBetBuilderKeys)));
        } else {
            setSessionBetBuilderTicket(null);
        }
    });

    watchEffect(() => {
        if (dynamicMarketBetSlip.value) {
            setSessionDynamicMarketTicket(pick<IDynamicMarketBetSlip>(dynamicMarketBetSlip.value, pickDynamicMarketKeys));
        } else {
            setSessionDynamicMarketTicket(null);
        }
    });

    return {
        singleBetSlip,
        singleTicket,
        addToSingleBetSlip,
        updateSingleBetSlip,
        removeSingleBetSlip,

        getParlayTicketKey,
        createParlayTicket,
        parlayBetSlip,
        addToParlayBetSlip,
        setParlayBetSlipTickets,
        addSingleToParlayBetSlip,
        updateParlayBetSlip,
        removeParlayTicket,
        removeParlayBetSlip,

        addToOutrightBetSlip,

        betBuilderBetSlip,
        addToBetBuilderBetSlip,
        setBetBuilderBetSlipTickets,
        updateBetBuilderBetSlip,
        removeBetBuilderBetSlip,
        removeBetBuilderTicket,

        dynamicMarketBetSlip,
        addToDynamicMarketBetSlip,
        updateDynamicMarketBetSlip,
        removeDynamicMarketBetSlip,

        isShowLastStake,
        setIsShowLastStake,
        singleLastStake,
        setSingleLastStake,
        parlayLastStake,
        setParlayLastStake,
    };
});
